'use client';

import { Product } from '@/lib/shopify/types';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';

// TODO: get these from shopify
export function getProductReviews(productId: string) {
  // Use the product ID to generate a consistent hash
  let hash = 0;
  for (let i = 0; i < productId.length; i++) {
    hash = ((hash << 5) - hash) + productId.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Generate a rating between 3.5 and 5.0 based on the hash
  const rating = (3.5 + (Math.abs(hash) % 15) / 10).toFixed(1);

  // Generate a review count between 5 and 120 based on the hash
  const reviewCount = 5 + (Math.abs(hash) % 115);

  return { rating, reviewCount };
}

export function ProductCard({ product }: { product: Product }) {
  const [isHovered, setIsHovered] = useState(false);

  // Get the first two images (if available)
  const primaryImage = product.featuredImage;
  const secondaryImage = product.images.length > 1 ? product.images[1] : null;

  // Check if there are multiple variants
  const hasMultipleVariants = product.variants.length > 1;

  // Get price display
  const price = product.priceRange.minVariantPrice.amount;
  const currency = product.priceRange.minVariantPrice.currencyCode;
  const formattedPrice = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(parseFloat(price));

  // Get consistent mock reviews based on product ID
  const { rating, reviewCount } = getProductReviews(product.id);

  return (
    <Link
      href={`/product/${product.handle}`}
      className="group block h-full w-full"
    >
      <div
        className="relative flex h-full w-full flex-col overflow-hidden rounded-lg bg-white transition-shadow duration-200 hover:shadow-md dark:bg-black"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative aspect-square overflow-hidden">
          {/* Primary Image */}
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: isHovered && secondaryImage ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0"
          >
            <Image
              src={primaryImage.url}
              alt={primaryImage.altText || product.title}
              fill
              sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
              className="object-cover"
              priority
            />
          </motion.div>

          {/* Secondary Image (shown on hover) */}
          {secondaryImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0"
            >
              <Image
                src={secondaryImage.url}
                alt={secondaryImage.altText || `${product.title} - alternate view`}
                fill
                sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw"
                className="object-cover"
              />
            </motion.div>
          )}
        </div>

        <div className="flex flex-col p-3">
          {/* Product Title */}
          <h3 className="text-sm font-medium text-black dark:text-white truncate">
            {product.title}
          </h3>

          {/* Price */}
          <p className="mt-1 text-sm font-medium text-black dark:text-white">
            {hasMultipleVariants ? `From ${formattedPrice}` : formattedPrice}
          </p>

          {/* Reviews */}
          <div className="mt-1 flex items-center">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className={`${i < Math.floor(parseFloat(rating)) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} mr-0.5`}
                />
              ))}
            </div>
            <span className="ml-1 text-xs text-gray-500">
              ({reviewCount})
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
